import React, { useEffect, useState, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import Skeleton from 'react-loading-skeleton'
import { trans } from '../../../../_providers/Translation'
import '../../../../assets/css/partials/status-diagram.css'
import { PieChart } from '@mui/x-charts/PieChart'
import { Button } from '@mui/material'
import DateRange from '@/components/partials/DateRange'
import CompactFilterLayout from '@/components/partials/CompactFilterLayout'
import filterImage from '@/assets/img/icons/filter.svg'
import { getColorVariationFromPrimaryColorBasedOnIndex } from '../../../../_helpers/calculateColor'

export default function InvoicePaymentsStats({
    endpoint = 'dashboard/invoices-payments/stats',
    title = 'Фактури',
    colors = ['#5563FF', '#A855F7', '#C084FC', '#E879F9'],
    donut = true,
    size = 220
}) {
    const [state, setState] = useState({ loading: true, data: null })
    const [counts, setCounts] = useState({
        sent: null,
        unsent: null,
        total: 0
    })
    const [dateRange, setDateRange] = useState({
        from: new Date().toISOString().split('T')[0],
        to: new Date().toISOString().split('T')[0]
    })

    const compactFilterLayoutRef = useRef(null)
    const fetchDebounceRef = useRef(null)

    const loadData = () => {
        setState(prev => ({ ...prev, loading: true }))
        const params = new URLSearchParams({
            date_from: dateRange.from,
            date_to: dateRange.to
        })

        Api.get(`${endpoint}?${params.toString()}`)
            .then(res => {
                const payload = res?.data ?? res
                setState({ loading: false, data: payload })
            })
            .catch(() => setState({ loading: false, data: null }))

        Api.get(`dashboard/sent-invoices/stats?${params.toString()}`)
            .then(res => {
                setCounts(res.data)
            })

    }

    useEffect(() => {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
            loadData()
        }, 300)
        return () => clearTimeout(fetchDebounceRef.current)
    }, [endpoint, dateRange])

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }))
    }

    function getAppliedFiltersCount() {
        return dateRange.from || dateRange.to ? 1 : 0
    }

    // Convert API data format to items array
    const apiData = state.data || {}

    const dataSource = Object.keys(apiData).length > 0 ? apiData : []

    // Convert object to items array
    const itemsSource = [
        {
            key: 'paid',
            label: trans('tabs.paid'),
            value: dataSource.paid?.count || 0,
            percentage: dataSource.paid?.percentage || 0,
            changePercent: dataSource.paid?.change_percent || null
        },
        {
            key: 'unpaid',
            label: trans('tabs.unpaid'),
            value: dataSource.unpaid?.count || 0,
            percentage: dataSource.unpaid?.percentage || 0,
            changePercent: dataSource.unpaid?.change_percent || null
        },
        {
            key: 'pending',
            label: trans('random.awaitingPaymentLabel'),
            value: dataSource.pending?.count || 0,
            percentage: dataSource.pending?.percentage || 0,
            changePercent: dataSource.pending?.change_percent || null
        }
    ]

    // Include all items for legend, but only render slices with values > 0
    const items = itemsSource
    const total = dataSource.total || items.reduce((s, i) => s + Number(i.value || 0), 0) || 1

    const pieData = items.map((it, idx) => ({
        id: idx,
        value: Number(it.value || 0),
        label: it.label,
        color: colors[idx % colors.length]
            ? `url(#invoice-gradient-${idx})`
            : colors[idx % colors.length],
    }))

    // For each slice: color1 is the base color (used for the legend),
    // color2 is the base color lightened a bit — both feed the gradient.
    function getSliceColors(color) {
        const color1 = color
        const color2 = color ? getColorVariationFromPrimaryColorBasedOnIndex(3, color) : color
        return { color1, color2 }
    }

    // Restore the base color on the tooltip swatch (a gradient url() can't be
    // used as a background-color, so the default marker would render empty).
    function PieTooltipContent({ itemData, series }) {
        const point = series.data[itemData.dataIndex]
        const baseColor = colors[itemData.dataIndex % colors.length]
        const value = Number(point?.value || 0)
        const pct = total ? ((value / total) * 100).toFixed(2) : 0
        return (
            <div className="status-pie-tooltip" style={{
                backgroundColor: "#1E1B39",
                borderRadius: "10px"
            }}>
                <span className="marker" style={{ backgroundColor: baseColor }} />
                <span className="label">{point?.label}</span>
                <span className="value" style={{ color: "#fff" }}>{`${value} (${pct}%)`}</span>
            </div>
        )
    }

    function getTotalResults() {
        return items.reduce((sum, it) => sum + Number(it.value || 0), 0)
    }

    return (
        <div className="invoices-pie-chart">
            <div className='head'>
                <h3>
                    {`${title} (${counts.total || 0})`}
                </h3>
                <div className='right'>
                    <Button onClick={(e) => {
                        compactFilterLayoutRef.current.open(e)
                    }}>
                        <img src={filterImage} alt="Filter" />
                        {getAppliedFiltersCount() ?
                            <i>{getAppliedFiltersCount()}</i>
                            : <></>}
                    </Button>
                    <CompactFilterLayout
                        ref={compactFilterLayoutRef}
                        appliedFiltersCount={getAppliedFiltersCount()}
                        onClearClick={() => setDateRange({ from: null, to: null })}
                    >
                        <div className='row'>
                            <div className='col'>
                                <DateRange
                                    startDate={dateRange.from || ''}
                                    endDate={dateRange.to || ''}
                                    onStartDateChange={(date) => handleDateChange('from', date)}
                                    onEndDateChange={(date) => handleDateChange('to', date)}
                                />
                            </div>
                        </div>
                    </CompactFilterLayout>
                </div>

            </div>
            <hr />
            <div className="body">
                {state.loading ?
                    <>
                        <div className='left-loading'>
                            <Skeleton height={200} count={1} />
                        </div>
                        <div className='right-loading'>
                            <Skeleton height={50} count={4} />
                        </div>
                    </>
                    :
                    getTotalResults() === 0 ?
                        <div
                            className="alert warning"
                            style={{
                                marginBottom: 0,
                                width: '100%',
                            }}
                        >
                            {trans("messages.noDataFound")}
                        </div>
                        :
                        <>
                            <div className='left'>
                                <PieChart
                                    series={[{
                                        data: pieData,
                                        valueFormatter: (item) => {
                                            const pct = total ? ((item.value / total) * 100).toFixed(2) : 0
                                            return `${item.value} (${pct}%)`
                                        },
                                    }]}
                                    sx={{
                                        '& .MuiPieArc-root': {
                                            stroke: 'none',
                                            strokeWidth: 0,
                                        },
                                    }}
                                    width={200}
                                    height={200}
                                    margin={{ right: 0 }}
                                    slotProps={{
                                        legend: { hidden: true },
                                    }}
                                    slots={{
                                        itemContent: PieTooltipContent,
                                    }}
                                >
                                    <defs>
                                        {items.map((it, idx) => {
                                            const { color1, color2 } = getSliceColors(colors[idx % colors.length])
                                            return (
                                                <linearGradient
                                                    key={idx}
                                                    id={`invoice-gradient-${idx}`}
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1"
                                                >
                                                    <stop offset="0%" stopColor={color1} />
                                                    <stop offset="100%" stopColor={color2} />
                                                </linearGradient>
                                            )
                                        })}
                                    </defs>
                                </PieChart>
                            </div>

                            <div className='right'>
                                <table>
                                    <tbody>

                                        {items.map((it, idx) => {
                                            const percentText = `${it.percentage?.toFixed(2) || 0}%`
                                            const changePercent = it.changePercent
                                            const changeClass = changePercent && changePercent.toString().startsWith('-') ? 'negative' : 'positive'
                                            const color = colors[idx % colors.length]
                                            return (
                                                <tr key={it.key || it.label || idx}>
                                                    <td>
                                                        <span style={{ background: color }} />
                                                    </td>
                                                    <td>
                                                        <b>
                                                            {it.label || it.name}
                                                        </b>
                                                    </td>
                                                    <td>
                                                        <div className='count'>
                                                            {it.value || 0}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className={`percentage ${changeClass}`}>
                                                            {`(${changePercent ? percentText : "0"}%)`}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                }
            </div>
            <hr />

            <div className='footer'>
                <p className='side-info'>
                    {`${trans('random.sentInvoicesLabel')}: ${counts.sent?.count || 0}(${counts.sent?.percentage}%)`}
                </p>
                <p className='side-info'>
                    {`${trans('random.unsentInvoicesLabel')}: ${counts.unsent?.count || 0}(${counts.unsent?.percentage}%)`}
                </p>
            </div>
        </div>
    )
}